import { IPublicClientApplication } from '@azure/msal-browser'
import axios from 'axios'
import { LOGIKSPARK_USERS_API_URL } from '../../app/axios/const'
import { loginRequest } from '../../authConfig'
import { IAcceptInvitationResponse, IInvitationPreview } from './invitationTypes'

// E10 F10-3 — the invitation preview is ANONYMOUS: the /invitation/:token landing renders before the
// invitee has signed in (they hold only the tokenised link), so the app's MSAL-bearer axios instances are
// unusable here. A bare instance with the token in the URL, mirroring features/Signup/signupApi.ts. The
// ACCEPT call, by contrast, is authenticated (the backend binds the oid from the JWT) and goes through the
// normal authed apiSlice mutation once the invitee has signed up.
const usersAnon = axios.create({
  baseURL: LOGIKSPARK_USERS_API_URL,
  headers: { 'Content-Type': 'application/json' },
})

// Responses use the ResponseDto envelope ({ data: <payload> }); unwrap via .data.data like signupApi.
// A 404 (unknown token) / 410 (expired) surfaces as an axios error the caller renders as an invalid-link
// state.
export const getInvitationPreview = async (
  token: string,
  signal?: AbortSignal
): Promise<IInvitationPreview> =>
  (await usersAnon.get(`/v1/invitations/${token}`, { signal })).data.data

// E10 F10-3 — AUTHED accept. The backend ([PersonaOptional]) binds the Entra oid from the bearer, so the
// page must send a token. The app's shared authed axios (usersAuthorizedFetch) is only wired up inside
// <App/>, which never mounts on this public /invitation route, so we acquire a token from the MSAL
// instance the page already holds and POST it on the same Users-host instance the preview uses. No body —
// the invitation token is in the URL and the oid is the bearer's.
export const acceptInvitation = async (
  token: string,
  instance: IPublicClientApplication
): Promise<IAcceptInvitationResponse> => {
  const account = instance.getActiveAccount()
  if (!account) {
    throw new Error('No active account to accept the invitation with')
  }
  const { accessToken } = await instance.acquireTokenSilent({ ...loginRequest, account })
  const res = await usersAnon.post(`/v1/invitations/${token}/accept`, undefined, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  return res.data.data
}

// E20 20.5.1 (WI 800) — home-realm discovery for the invitee's email, so Accept can route a federated
// domain through its organisation's IdP instead of minting a local credential. Anonymous, like the
// preview; the server resolves the Tenant from this origin's subdomain, which is why the call must go
// through the same-origin usersAnon instance. Mirrors CORE's HrdResponse (kind "local" | "federation").
export interface IHrdResponse {
  kind: string
  providerId?: number | null
}

export const getHrdForEmail = async (email: string): Promise<IHrdResponse> =>
  (await usersAnon.post('/v1/auth/hrd', { email })).data.data
